import { create } from 'zustand';
import { useAccounts, type Account } from '@wordpuppi/shared';

/** localStorage key for the account the user is currently "in" (the menu checkmark). */
const STORAGE_KEY = 'wpp-active-account';

function read(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

interface ActiveAccountState {
  pid: string | null;
  setPid: (pid: string | null) => void;
}

export const useActiveAccountStore = create<ActiveAccountState>((set) => ({
  pid: read(),
  setPid: (pid) => {
    try {
      if (pid) localStorage.setItem(STORAGE_KEY, pid);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private mode — the pick just lasts for this tab */
    }
    set({ pid });
  },
}));

/**
 * The account the user is working in, shared by the account menu, the Sites
 * filter and the Team / AI-keys pickers so switching in one place switches
 * them all (AB#1014).
 *
 * `pickedPid` is the explicit pick, honored only while it is still one of THIS
 * user's accounts — a pid left over from another session never wins. `active`
 * falls back owner-first, then to the first membership.
 */
export function resolveActive(accounts: Account[], pid: string | null) {
  const pickedPid = pid && accounts.some((a) => a.pid === pid) ? pid : null;
  const active =
    accounts.find((a) => a.pid === pickedPid) ??
    accounts.find((a) => a.role === 'owner') ??
    accounts[0];
  return { pickedPid, active };
}

/** True when the user is a non-owner member of `accountPid` (AB#1016–1018). Unknown/legacy pids → false. */
export function isMemberOf(accounts: Account[], accountPid?: string | null) {
  return !!accountPid && accounts.some((a) => a.pid === accountPid && a.role !== 'owner');
}

/**
 * Everything cached under an account — i.e. every query except the user's own
 * account LIST, which the menu needs to stay populated across a switch.
 */
export const isAccountScopedQuery = (q: { queryKey: readonly unknown[] }) =>
  !(q.queryKey[0] === 'accounts' && q.queryKey[1] === 'list');

export function useActiveAccount() {
  const { data, isLoading } = useAccounts();
  const accounts: Account[] = data?.data ?? [];
  const { pid, setPid } = useActiveAccountStore();
  return { accounts, isLoading, ...resolveActive(accounts, pid), setActive: setPid };
}
